import React, { type ReactNode } from 'react';
import { AppLoader } from './AppLoader';
import { AppScreen } from './AppScreen';
import {
  isReportGranted,
  REPORT_ACCESS_PERMISSIONS,
} from '../config/reportCapabilities';
import { PERMISSIONS } from '../constants/permissions';
import { useAuth } from '../context/AuthContext';
import { useReportAccess } from '../hooks/useReportAccess';
import { UnauthorizedScreen } from '../screens/system/UnauthorizedScreen';

type Props = {
  /** Code in rpt_role_report_access that grants this report to a role. */
  reportCode: string;
  children: ReactNode;
};

/**
 * Organization-wide report screens: open with EMPLOYEE_VIEW + a report
 * permission (HR Admin), or when the user's role was granted `reportCode`
 * (e.g. Employee). The API limits the latter to the user's own rows.
 */
export function ReportAccessGuard({ reportCode, children }: Props) {
  const auth = useAuth();
  const hasReportPermission = auth.hasAnyPermission([
    ...REPORT_ACCESS_PERMISSIONS,
  ]);
  const hasPermissionAccess =
    hasReportPermission && auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW);
  const { access, loading } = useReportAccess(
    hasReportPermission && !hasPermissionAccess,
  );

  if (hasPermissionAccess || isReportGranted(access, reportCode)) {
    return <>{children}</>;
  }
  if (loading) {
    return (
      <AppScreen>
        <AppLoader message="Loading report" />
      </AppScreen>
    );
  }
  return <UnauthorizedScreen />;
}
